import {action, observable} from 'mobx';
import http from '../../http'
import isPast from 'date-fns/isPast';
import {zonedTimeToUtc} from 'date-fns-tz'
import isToday from 'date-fns/isToday';
import isSameDay from 'date-fns/isSameDay';
import addDays from 'date-fns/addDays';
import startOfWeek from 'date-fns/startOfWeek';
import format from 'date-fns/format';
import ruLocale from 'date-fns/locale/ru';

export const BOOKING_SLOT_DAY_PARTS = [
    {id: 'night', label: 'Ночь', startHour: 0, endHour: 6},
    {id: 'morning', label: 'Утро', startHour: 6, endHour: 12},
    {id: 'day', label: 'День', startHour: 12, endHour: 18},
    {id: 'evening', label: 'Вечер', startHour: 18, endHour: 24},
];

const getSlotDayPartId = (time) => {
    const hour = parseInt(String(time).slice(0, 2), 10);
    const part = BOOKING_SLOT_DAY_PARTS.find((item) => hour >= item.startHour && hour < item.endHour);
    return part ? part.id : null;
};

const buildMonthLabel = (date) => format(date, 'LLLL yyyy', {locale: ruLocale}).replace(/^\S/, (m) => m.toUpperCase());
const isCurrentWeekRange = (dates = []) => dates.some((date) => date && date.is_today);
const parseLocalDateString = (value) => {
    if (!value || typeof value !== 'string') {
        return null;
    }

    const parts = value.split('-').map((item) => parseInt(item, 10));
    if (parts.length !== 3 || parts.some((item) => Number.isNaN(item))) {
        return null;
    }

    const [year, month, day] = parts;
    return new Date(year, month - 1, day, 12, 0, 0, 0);
};
const toLocalCalendarDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);

class BookingPageStore {
    _routerStore;
    _authStore;
    @observable currentDate = new Date();
    @observable selectedDate = new Date();
    @observable visibleWeekDate = new Date();
    @observable checkedDate = new Date();
    @observable loading = false;
    @observable firstLoad = true;
    @observable requestSend = false;
    @observable loadingFirst = true;
    @observable openAlert = false;
    @observable rules_check = true;
    @observable is404 = false;
    @observable buttonDisabled = true;
    @observable openModal = false;
    @observable has_google_sync = false;
    @observable alertMessage = '';
    @observable alertMessageType = 'success';
    @observable yandex_metrika_id = 'success';
    @observable hot_offer = null;
    @observable roomId = null;
    @observable scheduleId = null;
    @observable schedules = [];
    @observable price = null;
    @observable banner = null;
    @observable selectedFromIndex = null;
    @observable selectedToIndex = null;
    @observable resPrice = 0;
    @observable resSlotSize = 0;
    @observable slotCount = 0;
    @observable selectedFrom = 0;
    @observable selectedTo = 0;
    @observable discount = 0;
    @observable phone = '';
    @observable user_name = '';
    @observable user_name_init = false;
    @observable auth_required = false;
    @observable sms_required = false;
    @observable sms_code = '';
    @observable user_is_blocked = false;
    @observable show_sms_code_modal = false;
    @observable bookingRules = '';
    @observable bookingServiceType = 'app';
    @observable room = null;
    @observable timesFrom = [];
    @observable timesTo = [];
    @observable blockedDates = [];
    @observable equipments = [];
    @observable selectedEquipment = {};
    @observable equipmentLoading = false;
    @observable slotPicker = {
        version: 1,
        selection_mode: 'range',
        selected_date: '',
        month_label: '',
        window_size: 7,
        day_parts: BOOKING_SLOT_DAY_PARTS,
        dates: [],
        groups: [],
        range_constraints: {
            contiguous_only: true,
            respect_busy: true,
            respect_disabled: true,
        },
    };

    constructor(routerStore, authStore) {
        this._routerStore = routerStore
        this._authStore = authStore
    }

    normalizeSelectedDate = (date) => {
        const parsedDate = typeof date === 'string' ? parseLocalDateString(date) : null;
        const nextDate = parsedDate || (date ? new Date(date) : new Date());
        const today = toLocalCalendarDate(new Date());
        const normalizedDate = toLocalCalendarDate(nextDate);
        return normalizedDate < today ? today : normalizedDate;
    };

    load = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        let roomId = urlParams.get('roomId');
        if (!roomId) {
            const pathArray = window.location.pathname.split('/');
            const arrLength = pathArray.length;
            if (arrLength === 3
                || (arrLength === 5 && pathArray[3] === 'room')) {
                this.bookingServiceType = arrLength === 3 ? 'web' : pathArray[2]
                roomId = pathArray[arrLength - 1]
            }
        }

        if (this.firstLoad || roomId !== this.roomId) {
            Promise.resolve(() => true)
                .then(() => this.roomId = roomId)
                .then(() => this.selectedDate = this.normalizeSelectedDate(this.selectedDate))
                .then(() => this.visibleWeekDate = this.normalizeSelectedDate(this.visibleWeekDate))
                .then(() => this.firstLoad = false)
                .then(this.getSlots)
        }
    };

    getSlotsRequestDate = () => this.selectedDate || this.visibleWeekDate || new Date();

    getSlotsEndpoint = () => `/appointments/get-slots-ui?id=${this.roomId}&schedule_id=${this.scheduleId}&date=${this.getSlotsRequestDate().getTime()}&serviceType=${this.bookingServiceType}`;

    @action
    getSlots = () => {
        http.get(this.getSlotsEndpoint())
            .then(({data}) => {
                const responseContent = data.content;
                this.banner = responseContent.banner;
                this.bookingRules = responseContent.bookingRules;
                this.auth_required = responseContent.auth_required;
                this.sms_required = responseContent.sms_required;
                this.yandex_metrika_id = responseContent.yandex_metrika_id;
                this.hot_offer = responseContent.hot_offer;
                this.has_google_sync = responseContent.has_google_sync;
                if (this._authStore.user && !this._authStore.user.phone) {
                    this.phone = responseContent.phone ? responseContent.phone : '';
                }
                this.room = responseContent.room;
                this.scheduleId = responseContent.scheduleId;
                this.schedules = responseContent.schedules;
                this.user_is_blocked = responseContent.user_is_blocked;
                this.blockedDates = responseContent.blockedDates;
                this.timesFrom = responseContent.times ? responseContent.times.from : [];
                this.timesTo = responseContent.times ? responseContent.times.to : [];
                this.slotPicker = responseContent.slot_picker || this.buildLegacySlotPickerModel();
                // Инициализируем оборудование из ответа (без учета времени)
                this.equipments = responseContent.equipments || [];
                // Сбрасываем выбранное оборудование при загрузке новых слотов
                this.selectedEquipment = {};
            })
            .then(() => {
                this.loadingFirst = false;
                this.loading = false;
                this.buttonDisabled = true;
                this.selectedTo = 0;
                this.selectedFrom = 0;
                this.selectedToIndex = null;
                this.selectedFromIndex = null;
                this.slotCount = 0;
                return this.updatePrice()
            })
            .catch(({response}) => {
                this.is404 = response && response.status === 404
            })
    };

    @action
    getEquipment = () => {
        if (!this.selectedDate || !this.selectedFrom || !this.selectedTo || this.selectedFrom === '0' || this.selectedTo === '0') {
            // Если время не выбрано, используем оборудование из getSlots
            return;
        }

        this.equipmentLoading = true;

        http.get(`/appointments/get-equipment?id=${this.roomId}&date=${this.selectedDate.getTime()}&timeFrom=${this.selectedFrom}&timeTo=${this.selectedTo}`)
            .then(({data}) => {
                const responseContent = data.content;
                this.equipments = responseContent.equipments || [];
                // Сбрасываем выбранное оборудование при загрузке нового оборудования
                this.selectedEquipment = {};
            })
            .catch((error) => {
                console.error('Error loading equipment:', error);
                // В случае ошибки оставляем текущее оборудование
            })
            .finally(() => {
                this.equipmentLoading = false;
            });
    };

    @action
    setOrderCallback = () => {
        Promise.resolve()
            .then(() => {
                this.buttonDisabled = true;
                this.requestSend = true;
                if (this.sms_required && (!this.sms_code || this.sms_code.length !== 4)) {
                    http
                        .post(`/appointments/send-sms`, {
                            roomId: this.roomId,
                            phone: this.phone,
                        })
                        .then(() => {
                            this.show_sms_code_modal = true
                            this.sms_code = ''
                        })
                } else {
                    this.setOrder()
                }
            })
    }

    @action
    setOrder = () => {
        Promise.resolve()
            .then(() => {
                const equipmentData = this.equipmentForRequest;
                const totalPrice = (this.resPrice - this.discount) + this.equipmentTotalPrice;

                http
                    .post(`/appointments/order`, {
                        roomId: this.roomId,
                        sms_code: this.sms_code,
                        serviceType: this.bookingServiceType,
                        scheduleId: this.scheduleId,
                        slot_count: this.slotCount,
                        date: this.selectedDate.getTime(),
                        phone: this.phone,
                        user_name: this.user_name,
                        timeFrom: this.selectedFrom,
                        timeTo: this.selectedTo,
                        booking_service_type: this.bookingServiceType,
                        price: totalPrice,
                        equipment: equipmentData,
                    })
                    .then(({data}) => {
                        this.openAlert = true
                        this.requestSend = false
                        this.show_sms_code_modal = false
                        this.sms_code = ''
                        this.alertMessage = data.content.message
                        this.alertMessageType = 'success';
                        if (this._authStore.user && !this._authStore.user.phone) {
                            this._authStore.setUserPhone(this.phone.replace(/\+|\(|\)|-| /g, ''))
                        }
                    })
                    .then(() => this.getSlots())
                    .catch(({response}) => {
                        if (response && response.data && response.data.message) {
                            this.alertMessage = response.data.message
                        } else {
                            this.alertMessage = 'Что-то пошло не так :('
                        }
                        this.openAlert = true
                        this.requestSend = false
                        this.alertMessageType = 'error';
                    })
            });
    };

    disableDates = (date) => {
        let busyDay = false;
        date = zonedTimeToUtc(date, 'Europe/Moscow');

        if (this.blockedDates.length > 0) {
            this.blockedDates.forEach(value => {
                    if (isSameDay(date, value)) {
                        busyDay = true;
                    }
                }
            )
        }
        return (isPast(date) || busyDay) && !isToday(date);
    };

    clickHistoryButton = () => {
        const url = `/booking/cabinet`;
        if (this._authStore.checkAuth()) {
            this._routerStore.history.push(url)
        } else {
            this._authStore.authAndRedirect(url)
        }
    };

    checkBookingAuth = () => {
        if (!this.auth_required) {
            return true;
        }
        if (this._authStore.checkAuth()) {
            let phone = this.phone
            if (!phone && this._authStore.user && this._authStore.user.phone) {
                phone = this._authStore.user.phone;
            }
            this.phone = phone ? phone : ''
            if (!this.user_name_init) {
                this.user_name = this._authStore.user ? this._authStore.user.name : ''
                this.user_name_init = true
            }
            this.calcDisable();
            return true;
        }
        return false;
    }

    handleChangeSchedule = (id) => {
        if (id !== this.scheduleId) {
            Promise.resolve()
                .then(() => {
                    this.scheduleId = id
                    this.loading = true
                })
                .then(() => this.getSlots())
        }
    };

    handleDateChange = (date) => {
        if (!date) {
            return;
        }

        const normalizedDate = this.normalizeSelectedDate(date);

        Promise.resolve()
            .then(() => {
                this.selectedDate = normalizedDate
                this.visibleWeekDate = normalizedDate
                this.loading = true
            })
            .then(() => this.getSlots())
    };

    clearSlotSelection = () => {
        Promise.resolve()
            .then(() => {
                this.selectedFromIndex = null;
                this.selectedToIndex = null;
                this.selectedFrom = 0;
                this.selectedTo = 0;
                this.slotCount = 0;
                this.selectedEquipment = {};
            })
            .then(this.updatePrice)
            .then(this.calcDisable);
    };

    canExtendSlotRange = (startIndex, endIndex) => {
        if (startIndex === null || endIndex === null || endIndex < startIndex) {
            return false;
        }

        for (let index = startIndex; index <= endIndex; index++) {
            const fromSlot = this.timesFrom[index];
            const toSlot = this.timesTo[index];

            if (!fromSlot || !toSlot || fromSlot.busy || fromSlot.disabled || toSlot.busy || toSlot.disabled) {
                return false;
            }

            if (index > startIndex) {
                const previousToSlot = this.timesTo[index - 1];
                if (!previousToSlot || previousToSlot.time !== fromSlot.time) {
                    return false;
                }
            }
        }

        return true;
    };

    applySlotSelectionRange = (startIndex, endIndex) => {
        if (startIndex === null || endIndex === null || startIndex > endIndex) {
            this.selectedFromIndex = null;
            this.selectedToIndex = null;
            this.selectedFrom = 0;
            this.selectedTo = 0;
            return;
        }

        const fromSlot = this.timesFrom[startIndex];
        const toSlot = this.timesTo[endIndex];

        if (!fromSlot || !toSlot) {
            this.selectedFromIndex = null;
            this.selectedToIndex = null;
            this.selectedFrom = 0;
            this.selectedTo = 0;
            return;
        }

        this.selectedFromIndex = startIndex;
        this.selectedToIndex = endIndex;
        this.selectedFrom = fromSlot.time;
        this.selectedTo = toSlot.time;
    };

    removeSlotFromSelection = (index) => {
        if (this.selectedFromIndex === null || this.selectedToIndex === null) {
            return;
        }

        if (this.selectedFromIndex === this.selectedToIndex && this.selectedFromIndex === index) {
            this.applySlotSelectionRange(null, null);
            return;
        }

        if (index === this.selectedFromIndex) {
            this.applySlotSelectionRange(this.selectedFromIndex + 1, this.selectedToIndex);
            return;
        }

        if (index === this.selectedToIndex) {
            this.applySlotSelectionRange(this.selectedFromIndex, this.selectedToIndex - 1);
            return;
        }

        const leftStart = this.selectedFromIndex;
        const leftEnd = index - 1;
        const rightStart = index + 1;
        const rightEnd = this.selectedToIndex;
        const leftLength = leftEnd - leftStart + 1;
        const rightLength = rightEnd - rightStart + 1;

        if (leftLength <= 0 && rightLength <= 0) {
            this.applySlotSelectionRange(null, null);
            return;
        }

        if (rightLength > leftLength) {
            this.applySlotSelectionRange(rightStart, rightEnd);
            return;
        }

        this.applySlotSelectionRange(leftStart, leftEnd);
    };

    selectSlotByIndex = (index) => {
        if (index === null || index === undefined) {
            return;
        }

        const slot = this.timesFrom[index];
        const slotEnd = this.timesTo[index];

        if (!slot || !slotEnd || slot.busy || slot.disabled) {
            return;
        }

        Promise.resolve()
            .then(() => {
                if (this.selectedFromIndex !== null
                    && this.selectedToIndex !== null
                    && index >= this.selectedFromIndex
                    && index <= this.selectedToIndex) {
                    this.removeSlotFromSelection(index);
                    return;
                }

                if (this.selectedFromIndex === null || this.selectedToIndex === null) {
                    this.applySlotSelectionRange(index, index);
                    return;
                }

                const canExtendBackward = index === this.selectedFromIndex - 1
                    && this.canExtendSlotRange(index, this.selectedToIndex);

                if (canExtendBackward) {
                    this.applySlotSelectionRange(index, this.selectedToIndex);
                    return;
                }

                const canExtendForward = index === this.selectedToIndex + 1
                    && this.canExtendSlotRange(this.selectedFromIndex, index);

                if (canExtendForward) {
                    this.applySlotSelectionRange(this.selectedFromIndex, index);
                    return;
                }

                this.applySlotSelectionRange(index, index);
            })
            .then(() => {
                this.selectedEquipment = {};
            })
            .then(() => {
                if (this.selectedFrom && this.selectedTo && this.selectedFrom !== '0' && this.selectedTo !== '0') {
                    this.getEquipment();
                }
            })
            .then(this.updatePrice)
            .then(this.calcDisable);
    };

    shiftSlotPickerWindow = (direction) => {
        const dates = this.slotPicker && this.slotPicker.dates ? this.slotPicker.dates : [];
        if (!dates.length) {
            return;
        }

        const windowStep = 7;

        if (direction < 0) {
            if (isCurrentWeekRange(dates)) {
                return;
            }

            const currentDate = new Date(this.visibleWeekDate || this.getSlotsRequestDate());
            const previousDate = addDays(toLocalCalendarDate(currentDate), -windowStep);
            const fallbackDate = toLocalCalendarDate(new Date());

            Promise.resolve()
                .then(() => {
                    this.visibleWeekDate = (isPast(previousDate) && !isToday(previousDate)) ? fallbackDate : previousDate;
                    this.selectedDate = null;
                    this.loading = true;
                })
                .then(() => this.clearSlotSelection())
                .then(() => this.getSlots());
            return;
        }

        const nextDate = addDays(toLocalCalendarDate(new Date(this.visibleWeekDate || this.getSlotsRequestDate())), windowStep);
        Promise.resolve()
            .then(() => {
                this.visibleWeekDate = nextDate;
                this.selectedDate = null;
                this.loading = true;
            })
            .then(() => this.clearSlotSelection())
            .then(() => this.getSlots());
    };

    changeFrom = ({target}) => {
        let disabled = false;
        Promise.resolve()
            .then(() => {
                if (target.value !== 0) {
                    this.timesFrom.forEach((el, key) => {
                        if (target.value === el.time) {
                            this.selectedFromIndex = key;
                        }
                    })
                } else {
                    this.selectedFromIndex = null;
                }
            })
            .then(() => this.timesTo.forEach((el, key) => {
                el.disabled = key < this.selectedFromIndex || disabled;
                if (key >= this.selectedFromIndex && el.busy) {
                    disabled = true;
                }
            }))
            .then(() => this.selectedFrom = target.value)
            .then(() => {
                this.selectedToIndex = null
                this.selectedTo = 0
                // Сбрасываем выбранное оборудование при изменении времени начала
                this.selectedEquipment = {};
            })
            .then(this.updatePrice)
            .then(this.calcDisable);
    };


    changeTo = ({target}) => {
        Promise.resolve()
            .then(() => this.selectedTo = target.value)
            .then(() => {
                if (target.value !== 0) {
                    this.timesTo.forEach((el, key) => {
                        if (target.value === el.time) {
                            this.selectedToIndex = key;
                        }
                    })
                } else {
                    this.selectedToIndex = null;
                }
            })
            .then(() => {
                // Загружаем оборудование с учетом выбранного времени
                if (this.selectedFrom && this.selectedTo && this.selectedFrom !== '0' && this.selectedTo !== '0') {
                    this.getEquipment();
                }
            })
            .then(this.updatePrice)
            .then(this.calcDisable);
    };

    handleOnChange = (key, value) => {
        if (key === 'rules_check') {
            value = !this.rules_check;
        } else {
            value = (key === 'user_name' || key === 'sms_code') ? value.target.value : value;
        }
        Promise.resolve()
            .then(() => this[key] = value)
            .then(this.calcDisable);
    };

    handleModal = () => {
        this.openModal = !this.openModal
        this.rules_check = this.openModal ? this.rules_check : !this.rules_check;
    }

    calcDisable = () => {
        this.buttonDisabled = !(this.phone.replace(/\+|\(|\)|-| /g, '').length === 11
            && this.user_name
            && this.resSlotSize
            && this.resPrice
            && this.rules_check);
    };

    handleClose = () => {
        this.openAlert = false
    }

    buildLegacySlotPickerModel = () => {
        const selectedDate = this.selectedDate ? new Date(this.selectedDate) : null;
        const visibleWeekDate = this.visibleWeekDate ? new Date(this.visibleWeekDate) : new Date();
        const baseDate = selectedDate || visibleWeekDate;
        const today = toLocalCalendarDate(new Date());
        const blockedDateKeys = (this.blockedDates || []).map((value) => format(new Date(value), 'yyyy-MM-dd'));
        const weekStart = startOfWeek(toLocalCalendarDate(baseDate), {weekStartsOn: 1});
        const dayPartsMap = BOOKING_SLOT_DAY_PARTS.reduce((acc, part) => {
            acc[part.id] = {
                id: part.id,
                label: part.label,
                start_hour: part.startHour,
                end_hour: part.endHour,
                slots: [],
            };
            return acc;
        }, {});

        this.timesFrom.forEach((fromSlot, index) => {
            const toSlot = this.timesTo[index];
            if (!toSlot) {
                return;
            }

            const partId = getSlotDayPartId(fromSlot.time);
            if (!partId || !dayPartsMap[partId]) {
                return;
            }

            dayPartsMap[partId].slots.push({
                index,
                time_from: fromSlot.time,
                time_to: toSlot.time,
                busy: !!fromSlot.busy,
                disabled: !!fromSlot.disabled,
                slot_size: fromSlot.slot_size !== null && fromSlot.slot_size !== undefined ? parseInt(fromSlot.slot_size, 10) : null,
                price: fromSlot.price !== null && fromSlot.price !== undefined ? parseInt(fromSlot.price, 10) : null,
                discount_sum: fromSlot.discount_sum ? parseInt(fromSlot.discount_sum, 10) : 0,
                is_hot_offer: !!fromSlot.discount_sum,
            });
        });

        return {
            version: 1,
            selection_mode: 'range',
            selected_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
            selected_date_ts: selectedDate ? selectedDate.getTime() : null,
            month_label: buildMonthLabel(weekStart),
            window_size: 7,
            day_parts: BOOKING_SLOT_DAY_PARTS.map((part) => ({
                id: part.id,
                label: part.label,
                start_hour: part.startHour,
                end_hour: part.endHour,
            })),
            dates: Array.from({length: 7}, (_, index) => {
                const date = addDays(weekStart, index);
                const dateKey = format(date, 'yyyy-MM-dd');
                return {
                    date: dateKey,
                    timestamp_ms: date.getTime(),
                    weekday_short: format(date, 'EEEEEE', {locale: ruLocale}).toLowerCase(),
                    day_number: parseInt(format(date, 'd'), 10),
                    month_label: buildMonthLabel(date),
                    is_selected: selectedDate ? isSameDay(date, selectedDate) : false,
                    is_today: isSameDay(date, today),
                    is_disabled: (isPast(date) && !isToday(date)) || blockedDateKeys.includes(dateKey),
                };
            }),
            groups: BOOKING_SLOT_DAY_PARTS.map((part) => dayPartsMap[part.id]),
            range_constraints: {
                contiguous_only: true,
                respect_busy: true,
                respect_disabled: true,
            },
        };
    };

    updatePrice = () => {
        let discount = 0, price = 0, resPrice = 0, resSlotSize = 0;
        let slotCount = 0;
        return Promise.resolve()
            .then(() => {
                if (this.selectedToIndex !== null && this.selectedFromIndex !== null) {
                    slotCount = this.selectedToIndex - this.selectedFromIndex + 1;
                    for (let i = this.selectedFromIndex; i <= this.selectedToIndex; i++) {
                        discount += this.timesFrom[i].discount_sum;
                        resPrice += parseInt(this.timesFrom[i].price);
                        resSlotSize += parseInt(this.timesFrom[i].slot_size);
                        if (price === 0) {
                            price = this.timesFrom[i].price;
                        }
                        if (price && price !== this.timesFrom[i].price) {
                            price = null;
                        }
                    }
                }
            })
            .then(() => {
                this.resPrice = resPrice;
                this.price = price;
                this.resSlotSize = resSlotSize;
                this.slotCount = slotCount;
                this.discount = discount;
            });

    };

    @action
    setEquipment = (equipmentId, quantity) => {
        const updatedEquipment = {...this.selectedEquipment};
        if (quantity === 0) {
            delete updatedEquipment[equipmentId];
        } else {
            updatedEquipment[equipmentId] = quantity;
        }
        this.selectedEquipment = updatedEquipment;
        this.calcDisable();
    };

    @action
    setSelectedEquipment = (equipment) => {
        this.selectedEquipment = equipment;
        this.calcDisable();
    };

    // Вычисляем общую стоимость оборудования (с учетом количества слотов)
    get equipmentTotalPrice() {
        let total = 0;

        if (!this.equipments || !this.selectedEquipment || !this.slotCount) {
            return total;
        }

        // Создаем карту цен оборудования
        const priceMap = {};
        this.equipments.forEach(category => {
            category.equipment.forEach(item => {
                priceMap[item.id] = parseFloat(item.price) || 0;
            });
        });

        // Считаем общую стоимость с учетом количества слотов
        Object.entries(this.selectedEquipment).forEach(([equipmentId, quantity]) => {
            const price = priceMap[equipmentId] || 0;
            total += price * quantity * this.slotCount;
        });

        return total;
    }

    // Получаем детализированный список стоимости оборудования (с учетом количества слотов)
    get equipmentDetailedPrice() {
        const detailed = [];

        if (!this.equipments || !this.selectedEquipment || !this.slotCount) {
            return detailed;
        }

        // Создаем карту оборудования (id -> объект оборудования)
        const equipmentMap = {};
        this.equipments.forEach(category => {
            category.equipment.forEach(item => {
                equipmentMap[item.id] = item;
            });
        });

        // Формируем детализированный список
        Object.entries(this.selectedEquipment).forEach(([equipmentId, quantity]) => {
            if (quantity > 0) {
                const equipment = equipmentMap[equipmentId];
                if (equipment) {
                    const price = parseFloat(equipment.price) || 0;
                    const total = price * quantity * this.slotCount;
                    detailed.push({
                        id: equipmentId,
                        name: equipment.name,
                        quantity: quantity,
                        slots: this.slotCount,
                        pricePerUnit: price,
                        total: total
                    });
                }
            }
        });

        return detailed;
    }

    // Получаем данные оборудования для отправки на сервер
    get equipmentForRequest() {
        if (!this.selectedEquipment) {
            return [];
        }

        return Object.entries(this.selectedEquipment)
            .filter(([_, quantity]) => quantity > 0)
            .map(([id, quantity]) => ({
                id: parseInt(id),
                count: quantity
            }));
    }

}

export {BookingPageStore};
